import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  defaultDomainsFilter,
  defaultPublisherDomainsFilter,
  DomainsSortDirection,
  isDomainsSortProperty,
  type DomainsFilter,
  PublisherDomainStatus,
  type PublisherDomainsFilter,
} from './model';

export interface DomainsRouteSearch {
  search?: string;
  publishers?: string;
  statuses?: string;
  page?: number;
  size?: number;
  sortProperty?: string | null;
  sortDirection?: DomainsSortDirection;
}

export function domainsRouteSearchSchema(search: Record<string, unknown>): DomainsRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const publishers = typeof search['publishers'] === 'string' ? search['publishers'] : undefined;
  const statuses = typeof search['statuses'] === 'string' ? search['statuses'] : undefined;
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  const sortProperty = optionalTrimmedString(search['sortProperty']);
  const sortDirection = Object.values(DomainsSortDirection).includes(
    search['sortDirection'] as DomainsSortDirection,
  )
    ? (search['sortDirection'] as DomainsSortDirection)
    : undefined;

  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(publishers !== undefined ? { publishers } : {}),
    ...(statuses !== undefined ? { statuses } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty && isDomainsSortProperty(sortProperty) ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

export function parsePublisherDomainsRouteSearch(
  search: DomainsRouteSearch,
): PublisherDomainsFilter {
  const supportedStatuses = new Set<string>(Object.values(PublisherDomainStatus));
  return {
    searchTerm: search.search ?? defaultPublisherDomainsFilter.searchTerm,
    statuses: (search.statuses?.split(',') ?? []).filter(
      (status): status is PublisherDomainStatus => supportedStatuses.has(status),
    ),
    page: search.page ? search.page - 1 : defaultPublisherDomainsFilter.page,
    size: search.size ?? defaultPublisherDomainsFilter.size,
  };
}

export function serializePublisherDomainsRouteSearch(
  filter: PublisherDomainsFilter,
): DomainsRouteSearch {
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(filter.statuses.length ? { statuses: filter.statuses.join(',') } : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultPublisherDomainsFilter.size ? { size: filter.size } : {}),
  };
}

export function parseDomainsRouteSearch(search: DomainsRouteSearch): DomainsFilter {
  return {
    searchTerm: search.search ?? defaultDomainsFilter.searchTerm,
    publisherIds: search.publishers ? search.publishers.split(',').filter(Boolean) : [],
    page: search.page ? search.page - 1 : defaultDomainsFilter.page,
    size: search.size ?? defaultDomainsFilter.size,
    sortProperty:
      search.sortProperty === null ||
      (search.sortProperty && isDomainsSortProperty(search.sortProperty))
        ? search.sortProperty
        : defaultDomainsFilter.sortProperty,
    sortDirection: search.sortDirection ?? defaultDomainsFilter.sortDirection,
  };
}

export function serializeDomainsRouteSearch(filter: DomainsFilter): DomainsRouteSearch {
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(filter.publisherIds.length ? { publishers: filter.publisherIds.join(',') } : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultDomainsFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultDomainsFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultDomainsFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}
